<template>
  <PlantsTableContextMenu ref="ptcm" :selected-row="selectedRow"/>
  <DataTable
    :loading="loading"
    :value="paginatedResponse.items"
    paginator
    :first="firstRecord"
    :rows="paginatedResponse.itemsPerPage"
    selection-mode="single"
    :total-records="paginatedResponse.totalItems"
    :sort-field="sortBy"
    :sort-order="sortOrder"
    @row-click="onRowOpened"
    @sort="handleSort($event)"
    @page="emitPageChanged($event)"
    context-menu
    @rowContextmenu="handleContextMenuClicked"
    v-model:contextMenuSelection="selectedRow"
    showGridlines
    removableSort
    lazy
  >
    <template #empty> {{ $t('features.plants.empty') }}</template>

    <current-promise-status-column
      v-if="showColumn('plant.current_promise_status')"
      key="plant.current_promise_status"
      :company="company"
    />

    <status-since-column
      key="plant.status_since"
      v-if="showColumn('plant.status_since')"
    />

    <Column
      v-if="showColumn('plant.difference_till_now_pct')"
      field="plant.difference_till_now_pct"
      :header="$t('features.long_term.columns.current_difference')"
      sortable
      style="width: 10rem"
    >
      <template #body="slotProps">
        <promise-difference-cell
          :promise-periods="slotProps.data.plant.promise_statuses"
          type="pct"
        />
      </template>
    </Column>

    <Column
      v-if="showColumn('plant.difference_till_now_wh')"
      field="plant.difference_till_now_wh"
      :header="$t('features.long_term.columns.current_difference')"
      sortable
      style="width: 10rem"
    >
      <template #body="slotProps">
        <promise-difference-cell
          :promise-periods="slotProps.data.plant.promise_statuses"
          type="wh"
        />
      </template>
    </Column>

    <Column
      v-if="showColumn('plant.previous_promise_statuses')"
      field="plant.previous_promise_statuses"
      :header="$t('features.long_term.columns.previous_periods')"
      style="width: 12rem"
    >
      <template #body="slotProps">
        <promise-period-cell
          :company="company"
          :promise-periods="slotProps.data.plant.promise_statuses"
          periods-to-show="previous"
        />
      </template>
    </Column>

    <new-issue-column
      key="plant.new_issue"
      v-if="showColumn('plant.new_issue')"
    />

    <Column v-if="showColumn('plant.found_on')" field="plant.found_on" sortable>
      <template #header>
        {{ $t('glossary.found_on') }}
        <info-with-tooltip
          :title="$t('features.issues.found_on_tooltip')"
          class="ml-2"
        />
      </template>
      <template #body="slotProps">
        <found-on-cell :plant-json="slotProps.data.plant"/>
      </template>
    </Column>

    <Column
      v-if="showColumn('plant.existing_issues')"
      field="plant.existing_issues"
      :header="$t('features.filtering.names.issues_existing')"
      style="width: 12rem"
    >
      <template #body="slotProps">
        <existing-issues-cell :plant-json="slotProps.data.plant"/>
      </template>
    </Column>

    <Column
      v-if="showColumn('plant.snoozed_until')"
      field="plant.snoozed_until"
      :header="$t('glossary.snoozed_until')"
      style="width: 12rem"
    >
      <template #body="slotProps">
        <snoozed-until-cell
          :company-id="company.getId()"
          :plant-json="slotProps.data.plant"
          @fetch-required="$emit('fetch-required')"
        />
      </template>
    </Column>

    <issues-column
      key="plant.issues"
      v-if="showColumn('plant.issues')"
    />

    <Column
      v-if="showColumn('plant.name')"
      field="plant.name"
      :header="$t('global.name')"
      sortable
    >
      <template #body="slotProps">
        {{ slotProps.data.plant.name }}
      </template>
    </Column>

    <Column
      v-if="showColumn('plant.address.city')"
      field="plant.address.city"
      :header="$t('global.city')"
      sortable
    >
      <template #body="slotProps">
        {{ slotProps.data.plant.address.city }}
      </template>
    </Column>

    <Column
      v-if="showColumn('plant.address.street')"
      field="plant.address.street"
      :header="$t('global.street')"
      sortable
    >
      <template #body="slotProps">
        {{ slotProps.data.plant.address.street }}
      </template>
    </Column>

    <Column
      v-if="showColumn('plant.address.street_number')"
      field="plant.address.street_number"
      :header="$t('global.house_number')"
      sortable
    >
      <template #body="slotProps">
        {{ slotProps.data.plant.address.street_number }}
      </template>
    </Column>

    <Column
      v-if="showColumn('plant.address.postal_code')"
      field="plant.address.postal_code"
      :header="$t('global.postal_code')"
      style="width: 11rem"
    >
      <template #body="slotProps">
        {{ slotProps.data.plant.address.postal_code }}
      </template>
    </Column>

    <Column
      v-if="showColumn('plant.plant_code')"
      field="plant.plant_code"
      :header="$t('glossary.plant_code')"
      style="width: 11rem"
    >
      <template #body="slotProps">
        {{ slotProps.data.plant.plant_code }}
      </template>
    </Column>

    <create-ticket-column
      key="plant.create_ticket"
      v-if="showColumn('plant.create_ticket')"
      :company-id="company.getId()"
      @ticket-created="$emit('fetch-required')"
    />

    <Column
      v-if="showColumn('plant.tickets')"
      field="plant.tickets"
      header="Tickets"
    >
      <template #body="slotProps">
        <tickets-cell :tickets-json="slotProps.data.plant.tickets"/>
      </template>
    </Column>

    <yield-score-column
      dataKey="yield_score_7d"
      translationKey="performance_ratio_7d"
      v-if="showColumn('plant.yield_score_7d')"
    />

    <yield-score-column
      dataKey="yield_score_30d"
      translationKey="performance_ratio_30d"
      v-if="showColumn('plant.yield_score_30d')"
    />

    <Column
      v-if="showColumn('plant.tags')"
      field="plant.tags"
      :header="$t('glossary.tag', 2)"
    >
      <template #body="slotProps">
        <Tag
          v-for="(tag, index) in slotProps.data.plant.tags"
          :key="index"
          class="mr-1"
        >
          {{ tag.name }}
        </Tag>
      </template>
    </Column>

    <Column
      v-if="showColumn('plant.driver_accounts')"
      field="plant.driver_accounts"
      :header="$t('glossary.driver_account', 2)"
    >
      <template #body="slotProps">
        <driver-accounts-cell :plant-json="slotProps.data.plant"/>
      </template>
    </Column>

    <Column
      v-if="showColumn('plant.attached_companies')"
      field="plant.attached_companies"
      :header="$t('glossary.attached_companies')"
    >
      <template #body="slotProps">
        <attached-companies-cell
          :companies="slotProps.data.plant.companies"
          :user-company-id="company.getId()"
        />
      </template>
    </Column>

    <custom-fields-column
      key="plant.custom_fields"
      v-if="showColumn('plant.custom_fields')"
    />

    <Column
      v-if="showColumn('plant.peak_in_watt')"
      field="plant.peak_in_watt"
      :header="$t('glossary.peak_total')"
      sortable
    >
      <template #body="slotProps">
        <total-peak-in-watt-cell :meters-json="slotProps.data.plant.meters"/>
      </template>
    </Column>

    <Column
      v-if="showColumn('plant.monitored_since')"
      field="plant.monitored_since"
      :header="$t('glossary.monitored_since')"
      sortable
      style="width: 11rem"
    >
      <template #body="slotProps">
        {{ slotProps.data.plant.monitored_since }}
      </template>
    </Column>

    <!-- TICKETS-->

    <Column
      v-if="showColumn('ticket.assignees')"
      field="ticket.assignees"
      :header="$t('global.assigned_to')"
    >
      <template #body="slotProps">
        <user-avatar
          v-for="assignee in slotProps.data.ticket.assignees"
          :key="'assignee' + slotProps.data.ticket.id + '/' + assignee.id"
          :user="asUser(assignee)"
          style="margin-right: 2px"
          v-tooltip="`${assignee.first_name} ${assignee.last_name}`"
        />
      </template>
    </Column>

    <Column
      v-if="showColumn('ticket.title')"
      field="ticket.title"
      :header="$t('global.title')"
    >
      <template #body="slotProps">
        <span v-tooltip="stripTags(slotProps.data.description, true)">
          {{ slotProps.data.ticket.title }}
        </span>
      </template>
    </Column>

    <Column
      v-if="showColumn('ticket.status')"
      field="ticket.status"
      :header="$t('global.status')"
    >
      <template #body="slotProps">
        <Tag :severity="slotProps.data.ticket.closed_on ? 'error' : 'success'">
          {{
            slotProps.data.ticket.closed_on
              ? $t('global.closed')
              : $t('global.open')
          }}
        </Tag>
      </template>
    </Column>

    <Column
      v-if="showColumn('ticket.company_name')"
      field="ticket.company_name"
      :header="$t('global.company')"
    >
      <template #body="slotProps">
        <Button
          @click="
            setCompanyAndReload(
              slotProps.data.ticket.company,
              slotProps.data.ticket.id
            )
          "
          size="small"
          v-tooltip="'Go to ticket and switch company'"
        >
          {{ slotProps.data.ticket.company.name }}
        </Button>
      </template>
    </Column>

    <Column
      v-if="showColumn('ticket.created_at')"
      field="ticket.created_at"
      :header="$t('global.created_at')"
      sortable
    >
      <template #body="slotProps">
        <span
          v-tooltip="
            formatAsDateTime(new Date(slotProps.data.ticket.created_at))
          "
        >
          {{ formatAsDate(new Date(slotProps.data.ticket.created_at)) }}
        </span>
      </template>
    </Column>

    <Column
      v-if="showColumn('ticket.closed_on')"
      field="ticket.closed_on"
      :header="$t('global.closed_on')"
    >
      <template #body="slotProps">
        <span
          v-tooltip="
            slotProps.data.ticket.closed_on !== null
              ? formatAsDateTime(new Date(slotProps.data.ticket.closed_on))
              : ''
          "
        >
          {{
            slotProps.data.ticket.closed_on !== null
              ? formatAsDate(new Date(slotProps.data.ticket.closed_on))
              : ''
          }}
        </span>
      </template>
    </Column>

    <Column
      v-if="showColumn('ticket.attached_companies')"
      :header="$t('glossary.attached_companies')"
    >
      <template #body="slotProps">
        <Tag
          :key="company.id"
          v-for="company in slotProps.data.ticket.companies.filter(
            (c) => !c.is_owner
          )"
          class="mr-2 my-1"
        >
          {{ company.name }}
        </Tag>
      </template>
    </Column>
  </DataTable>
</template>

<script lang="ts">
import type { DataTableRowContextMenuEvent } from 'primevue';
import { useCompanyStore } from '@/stores/CompanyStore.ts';
import Company from '@/domain/model/business/Company';
import User from '@/domain/model/business/User.ts';
import {
  formatAsDate,
  formatAsDateTime,
} from '@/infrastructure/support/utils/DateTimeUtils.ts';
import InfoWithTooltip from '@/presentation/components/InfoWithTooltip.vue';
import PlantIssues from '@/presentation/components/status/icon/PlantIssues.vue';
import CreateTicketFromPlantJsonButton from '@/presentation/components/tickets/CreateTicketFromPlantJsonButton.vue';
import UserAvatar from '@/presentation/components/users/UserAvatar.vue';
import HardReload from '@/presentation/mixins/HardReload';
import RouterHelper from '@/presentation/mixins/RouterHelper.js';
import StringUtils from '@/infrastructure/support/utils/StringUtils.ts';
import AttachedCompaniesCell from '@/presentation/pages/resources/cells/AttachedCompaniesCell.vue';
import DriverAccountsCell from '@/presentation/pages/resources/cells/DriverAccountsCell.vue';
import ExistingIssuesCell from '@/presentation/pages/resources/cells/ExistingIssuesCell.vue';
import FoundOnCell from '@/presentation/pages/resources/cells/FoundOnCell.vue';
import IssuesCell from '@/presentation/pages/resources/cells/IssuesCell.vue';
import NewIssueCell from '@/presentation/pages/resources/cells/NewIssueCell.vue';
import PromiseDifferenceCell from '@/presentation/pages/resources/cells/PromiseDifferenceCell.vue';
import PromisePeriodCell from '@/presentation/pages/resources/cells/PromisePeriodCell.vue';
import PromisePeriodChangedOnCell from '@/presentation/pages/resources/cells/PromisePeriodChangedOnCell.vue';
import SnoozedUntilCell from '@/presentation/pages/resources/cells/SnoozedUntilCell.vue';
import TicketsCell from '@/presentation/pages/resources/cells/TicketsCell.vue';
import TotalPeakInWattCell from '@/presentation/pages/resources/cells/TotalPeakInWattCell.vue';
import CreateTicketColumn from '@/presentation/pages/resources/columns/CreateTicketColumn.vue';
import CurrentPromiseStatusColumn from '@/presentation/pages/resources/columns/CurrentPromiseStatusColumn.vue';
import CustomFieldsColumn from '@/presentation/pages/resources/columns/CustomFieldsColumn.vue';
import IssuesColumn from '@/presentation/pages/resources/columns/IssuesColumn.vue';
import NewIssueColumn from '@/presentation/pages/resources/columns/NewIssueColumn.vue';
import StatusSinceColumn from '@/presentation/pages/resources/columns/StatusSinceColumn.vue';
import YieldScoreColumn from '@/presentation/pages/resources/columns/YieldScoreColumn.vue';
import PaginatedResponse from '@/presentation/pages/resources/PaginatedResponse.ts';
import PlantsTableContextMenu from '@/presentation/pages/resources/PlantsTableContextMenu.vue';

export default {
  props: {
    paginatedResponse: { type: PaginatedResponse },
    company: { type: Company },
    loading: { type: Boolean },
    shownFields: { type: Array<string> },
    sortBy: { type: String },
    sortOrder: { type: Number },
    currentPage: { type: Number },
  },
  name: 'PlantsTable',
  components: {
    PlantsTableContextMenu,
    CreateTicketColumn,
    StatusSinceColumn,
    YieldScoreColumn,
    CustomFieldsColumn,
    CurrentPromiseStatusColumn,
    NewIssueColumn,
    IssuesColumn,
    SnoozedUntilCell,
    PromisePeriodChangedOnCell,
    ExistingIssuesCell,
    NewIssueCell,
    PromiseDifferenceCell,
    TotalPeakInWattCell,
    AttachedCompaniesCell,
    TicketsCell,
    DriverAccountsCell,
    FoundOnCell,
    IssuesCell,
    PromisePeriodCell,
    CreateTicketFromPlantJsonButton,
    InfoWithTooltip,
    PlantIssues,
    UserAvatar,
  },
  emits: ['row-opened', 'page-changed', 'sort-changed', 'fetch-required'],
  mixins: [HardReload, RouterHelper],
  data() {
    return {
      selectedRow: null,
    };
  },
  computed: {
    fields() {
      const fieldOptions = {
        issues: { key: 'plant.issues' },
        new_issue: { key: 'plant.new_issue' },
        existing_issues: { key: 'plant.existing_issues' },
        snoozed_until: { key: 'plant.snoozed_until' },
        yield_score_7d: { key: 'plant.yield_score_7d' },
        yield_score_30d: { key: 'plant.yield_score_30d' },
        name: { key: 'plant.name' },
        city: { key: 'plant.address.city' },
        street: { key: 'plant.address.street' },
        street_number: { key: 'plant.address.street_number' },
        found_on: { key: 'plant.found_on', sortDirection: 'desc' },
        create_ticket: { key: 'plant.create_ticket' },
        current_promise_status: { key: 'plant.current_promise_status' },
        status_since: { key: 'plant.status_since' },
        difference_till_now_pct: { key: 'plant.difference_till_now_pct' },
        difference_till_now_wh: { key: 'plant.difference_till_now_wh' },
        previous_promise_statuses: { key: 'plant.previous_promise_statuses' },
        custom_fields: { key: 'plant.custom_fields', label: 'Custom Fields' },
        plant_code: { key: 'plant.plant_code', label: 'Plant Code' },
        tags: { key: 'plant.tags', label: 'Tags' },
        driver_accounts: { key: 'plant.driver_accounts', label: 'Drivers' },
        tickets: { key: 'plant.tickets', label: 'Tickets' },
        postal_code: { key: 'plant.address.postal_code', label: 'Postal Code' },
        attached_companies: {
          key: 'plant.attached_companies',
          label: 'Attached Companies',
        },
        peak_in_watt: { key: 'plant.peak_in_watt', label: 'Total Peak' },
        monitored_since: { key: 'plant.monitored_since' },
        // TICKETS
        assignees: { key: 'ticket.assignees', thStyle: { width: '9rem' } },
        title: { key: 'ticket.title' },
        status: { key: 'ticket.status', thStyle: { width: '9rem' } },
        created_at: {
          key: 'ticket.created_at',
          thStyle: { width: '9rem' },
          sortable: true,
        },
        closed_on: { key: 'ticket.closed_on', thStyle: { width: '9rem' } },
        company: { key: 'ticket.company_name', label: 'Company' },
      };
      return this.shownFields.map((fieldKey: string) => fieldOptions[fieldKey]);
    },
    firstRecord() {
      const zeroIndexedPage = this.currentPage - 1;
      return zeroIndexedPage * this.paginatedResponse.itemsPerPage;
    },
  },
  methods: {
    stripTags: StringUtils.stripTags,
    formatAsDateTime,
    formatAsDate,
    showColumn(name) {
      return this.fields.some((field: any) => field.key === name);
    },
    onRowOpened(event) {
      this.$emit('row-opened', event.data, event.index, event);
    },
    handleSort(event) {
      this.$emit('sort-changed', event);
    },
    emitPageChanged(event) {
      this.$emit('page-changed', event.page + 1);
    },
    setCompanyAndReload(company, ticketId) {
      useCompanyStore().set(company);
      this.reload(
        'SingleTicket',
        {},
        {
          ticketId: parseInt(ticketId),
        },
      );
    },
    handleContextMenuClicked(event: DataTableRowContextMenuEvent) {
      this.$refs.ptcm.contextMenu?.show(event.originalEvent);
    },
    asUser(assignee: any) {
      return new User(
        assignee.id,
        assignee.email,
        assignee.first_name,
        assignee.last_name,
      );
    },
  },
};
</script>

<style></style>
